import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { ArrowLeft, Save, HelpCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FichaSemanal({ user, onLogout }) {
  const { corretorId } = useParams();
  const navigate = useNavigate();
  const [corretor, setCorretor] = useState(null);
  const [scorecards, setScorecards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    semana: 1,
    modulo: 1,
    periodo_inicio: '',
    periodo_fim: '',
    metricas: {
      ligacoes: 0,
      visitas_1: 0,
      visitas_2: 0,
      captacoes: 0,
      estudos: 0,
      posts: 0
    },
    uso_c2s: false,
    uso_pipeimob: false,
    uso_ilist: false,
    uso_brokerapp: false,
    pontos_travamento: '',
    necessidade_reforco: '',
    qualidade: {
      disciplina: 0,
      consistencia: 0,
      postura_profissional: 0,
      evolucao: 0
    },
    resumo: {
      o_que_funcionou: '',
      o_que_nao_funcionou: '',
      o_que_fara_diferente: '',
      duvidas: '',
      decisao: 'Normal'
    },
    metas_proxima_semana: {
      ligacoes: 0,
      visitas_1: 0,
      visitas_2: 0,
      captacoes: 0,
      estudos: 0,
      posts: 0
    },
    presenca_dias: 0
  });

  useEffect(() => {
    loadData();
  }, [corretorId]);

  const loadData = async () => {
    try {
      const [corretorRes, scorecardsRes] = await Promise.all([
        axios.get(`${API}/corretores/${corretorId}`),
        axios.get(`${API}/scorecard/corretor/${corretorId}`)
      ]);
      
      setCorretor(corretorRes.data);
      setScorecards(scorecardsRes.data);
      
      // Initialize form with corretor's current data
      setFormData(prev => ({
        ...prev,
        semana: corretorRes.data.semana_atual,
        modulo: corretorRes.data.modulo_atual,
        periodo_inicio: new Date().toISOString().split('T')[0],
        periodo_fim: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      }));
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await axios.post(`${API}/scorecard`, {
        corretor_id: corretorId,
        broker_id: user.id,
        broker_nome: user.nome,
        ...formData
      });
      
      toast.success('Scorecard salvo com sucesso!');
      navigate(-1);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar scorecard');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-remax-blue"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
                data-testid="back-button"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <h1 className="text-xl font-bold" style={{ fontFamily: 'Work Sans', color: '#003DA5' }}>
                  Ficha Semanal - {corretor?.nome}
                </h1>
                <p className="text-sm text-gray-500">Semana {formData.semana} | Módulo {formData.modulo}</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Cabecalho */}
          <Card>
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Work Sans' }}>Informações da Semana</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Semana do Programa</Label>
                <Input
                  type="number"
                  min="1"
                  max="12"
                  value={formData.semana}
                  onChange={(e) => setFormData({ ...formData, semana: parseInt(e.target.value) })}
                  data-testid="semana-input"
                />
              </div>
              <div>
                <Label>Módulo</Label>
                <Input
                  type="number"
                  min="1"
                  max="5"
                  value={formData.modulo}
                  onChange={(e) => setFormData({ ...formData, modulo: parseInt(e.target.value) })}
                  data-testid="modulo-input"
                />
              </div>
              <div>
                <Label>Período Início</Label>
                <Input
                  type="date"
                  value={formData.periodo_inicio}
                  onChange={(e) => setFormData({ ...formData, periodo_inicio: e.target.value })}
                  data-testid="periodo-inicio-input"
                />
              </div>
              <div>
                <Label>Período Fim</Label>
                <Input
                  type="date"
                  value={formData.periodo_fim}
                  onChange={(e) => setFormData({ ...formData, periodo_fim: e.target.value })}
                  data-testid="periodo-fim-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Metricas da Semana */}
          <Card>
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Work Sans' }}>Números da Semana</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Label className="flex items-center gap-1 cursor-help">
                        Contatos
                        <HelpCircle className="w-3.5 h-3.5 text-gray-400" />
                      </Label>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Novos contatos de proprietário</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <Input
                  type="number"
                  min="0"
                  value={formData.metricas.ligacoes}
                  onChange={(e) => setFormData({
                    ...formData,
                    metricas: { ...formData.metricas, ligacoes: parseInt(e.target.value) || 0 }
                  })}
                  data-testid="ligacoes-input"
                />
              </div>
              <div>
                <Label>Visitas de Captação</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.metricas.visitas_1}
                  onChange={(e) => setFormData({
                    ...formData,
                    metricas: { ...formData.metricas, visitas_1: parseInt(e.target.value) || 0 }
                  })}
                  data-testid="visitas1-input"
                />
              </div>
              <div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Label className="flex items-center gap-1 cursor-help">
                        CRE
                        <HelpCircle className="w-3.5 h-3.5 text-gray-400" />
                      </Label>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Contrato de Representação Exclusiva</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <Input
                  type="number"
                  min="0"
                  value={formData.metricas.captacoes}
                  onChange={(e) => setFormData({
                    ...formData,
                    metricas: { ...formData.metricas, captacoes: parseInt(e.target.value) || 0 }
                  })}
                  data-testid="captacoes-input"
                />
              </div>
              <div>
                <Label>Estudos de Mercado</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.metricas.estudos}
                  onChange={(e) => setFormData({
                    ...formData,
                    metricas: { ...formData.metricas, estudos: parseInt(e.target.value) || 0 }
                  })}
                  data-testid="estudos-input"
                />
              </div>
              <div>
                <Label>Presença na Silver</Label>
                <Input
                  type="number"
                  min="0"
                  max="7"
                  value={formData.presenca_dias}
                  onChange={(e) => setFormData({ ...formData, presenca_dias: parseInt(e.target.value) || 0 })}
                  data-testid="presenca-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Ferramentas */}
          <Card>
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Work Sans' }}>Ferramentas / Tecnologia</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.uso_c2s}
                    onChange={(e) => setFormData({ ...formData, uso_c2s: e.target.checked })}
                    className="w-4 h-4"
                    data-testid="uso-c2s-checkbox"
                  />
                  <span>Uso do C2S</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.uso_pipeimob}
                    onChange={(e) => setFormData({ ...formData, uso_pipeimob: e.target.checked })}
                    className="w-4 h-4"
                    data-testid="uso-pipeimob-checkbox"
                  />
                  <span>Uso do PipeImob</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.uso_ilist}
                    onChange={(e) => setFormData({ ...formData, uso_ilist: e.target.checked })}
                    className="w-4 h-4"
                    data-testid="uso-ilist-checkbox"
                  />
                  <span>Uso do iList</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.uso_brokerapp}
                    onChange={(e) => setFormData({ ...formData, uso_brokerapp: e.target.checked })}
                    className="w-4 h-4"
                    data-testid="uso-brokerapp-checkbox"
                  />
                  <span>Uso do BrokerApp</span>
                </label>
              </div>
              <div>
                <Label>Pontos de Travamento</Label>
                <Textarea
                  value={formData.pontos_travamento}
                  onChange={(e) => setFormData({ ...formData, pontos_travamento: e.target.value })}
                  rows={2}
                  data-testid="travamento-textarea"
                />
              </div>
              <div>
                <Label>Necessidade de Reforço</Label>
                <Textarea
                  value={formData.necessidade_reforco}
                  onChange={(e) => setFormData({ ...formData, necessidade_reforco: e.target.value })}
                  rows={2}
                  data-testid="reforco-textarea"
                />
              </div>
            </CardContent>
          </Card>

          {/* Qualidade */}
          <Card>
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Work Sans' }}>Qualidade e Postura (1-5)</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label>Disciplina</Label>
                <Input
                  type="number"
                  min="0"
                  max="5"
                  value={formData.qualidade.disciplina}
                  onChange={(e) => setFormData({
                    ...formData,
                    qualidade: { ...formData.qualidade, disciplina: parseInt(e.target.value) || 0 }
                  })}
                  data-testid="disciplina-input"
                />
              </div>
              <div>
                <Label>Consistência</Label>
                <Input
                  type="number"
                  min="0"
                  max="5"
                  value={formData.qualidade.consistencia}
                  onChange={(e) => setFormData({
                    ...formData,
                    qualidade: { ...formData.qualidade, consistencia: parseInt(e.target.value) || 0 }
                  })}
                  data-testid="consistencia-input"
                />
              </div>
              <div>
                <Label>Postura Profissional</Label>
                <Input
                  type="number"
                  min="0"
                  max="5"
                  value={formData.qualidade.postura_profissional}
                  onChange={(e) => setFormData({
                    ...formData,
                    qualidade: { ...formData.qualidade, postura_profissional: parseInt(e.target.value) || 0 }
                  })}
                  data-testid="postura-input"
                />
              </div>
              <div>
                <Label>Evolução</Label>
                <Input
                  type="number"
                  min="0"
                  max="5"
                  value={formData.qualidade.evolucao}
                  onChange={(e) => setFormData({
                    ...formData,
                    qualidade: { ...formData.qualidade, evolucao: parseInt(e.target.value) || 0 }
                  })}
                  data-testid="evolucao-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Resumo Semanal */}
          <Card>
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Work Sans' }}>Resumo e Encaminhamento</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>O que funcionou bem?</Label>
                <Textarea
                  value={formData.resumo.o_que_funcionou}
                  onChange={(e) => setFormData({
                    ...formData,
                    resumo: { ...formData.resumo, o_que_funcionou: e.target.value }
                  })}
                  rows={3}
                  data-testid="funcionou-textarea"
                />
              </div>
              <div>
                <Label>O que não funcionou?</Label>
                <Textarea
                  value={formData.resumo.o_que_nao_funcionou}
                  onChange={(e) => setFormData({
                    ...formData,
                    resumo: { ...formData.resumo, o_que_nao_funcionou: e.target.value }
                  })}
                  rows={3}
                  data-testid="nao-funcionou-textarea"
                />
              </div>
              <div>
                <Label>O que fará diferente na próxima semana?</Label>
                <Textarea
                  value={formData.resumo.o_que_fara_diferente}
                  onChange={(e) => setFormData({
                    ...formData,
                    resumo: { ...formData.resumo, o_que_fara_diferente: e.target.value }
                  })}
                  rows={3}
                  data-testid="diferente-textarea"
                />
              </div>
              <div>
                <Label>Dúvidas sobre técnica/modelo/ferramenta</Label>
                <Textarea
                  value={formData.resumo.duvidas}
                  onChange={(e) => setFormData({
                    ...formData,
                    resumo: { ...formData.resumo, duvidas: e.target.value }
                  })}
                  rows={3}
                  data-testid="duvidas-textarea"
                />
              </div>
              <div>
                <Label>Decisão da Semana</Label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={formData.resumo.decisao}
                  onChange={(e) => setFormData({
                    ...formData,
                    resumo: { ...formData.resumo, decisao: e.target.value }
                  })}
                  data-testid="decisao-select"
                >
                  <option value="Normal">Segue Normal</option>
                  <option value="Alerta Amarelo">Alerta Amarelo</option>
                  <option value="Alerta Vermelho">Alerta Vermelho</option>
                  <option value="Descontinuado">Descontinuado</option>
                </select>
              </div>
            </CardContent>
          </Card>

          {/* Metas Proxima Semana */}
          <Card>
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Work Sans' }}>Metas da Próxima Semana</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Label className="flex items-center gap-1 cursor-help">
                        Contatos
                        <HelpCircle className="w-3.5 h-3.5 text-gray-400" />
                      </Label>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Novos contatos de proprietário</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <Input
                  type="number"
                  min="0"
                  value={formData.metas_proxima_semana.ligacoes}
                  onChange={(e) => setFormData({
                    ...formData,
                    metas_proxima_semana: { ...formData.metas_proxima_semana, ligacoes: parseInt(e.target.value) || 0 }
                  })}
                  data-testid="meta-ligacoes-input"
                />
              </div>
              <div>
                <Label>Visitas de Captação</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.metas_proxima_semana.visitas_1}
                  onChange={(e) => setFormData({
                    ...formData,
                    metas_proxima_semana: { ...formData.metas_proxima_semana, visitas_1: parseInt(e.target.value) || 0 }
                  })}
                  data-testid="meta-visitas1-input"
                />
              </div>
              <div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Label className="flex items-center gap-1 cursor-help">
                        CRE
                        <HelpCircle className="w-3.5 h-3.5 text-gray-400" />
                      </Label>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Contrato de Representação Exclusiva</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <Input
                  type="number"
                  min="0"
                  value={formData.metas_proxima_semana.captacoes}
                  onChange={(e) => setFormData({
                    ...formData,
                    metas_proxima_semana: { ...formData.metas_proxima_semana, captacoes: parseInt(e.target.value) || 0 }
                  })}
                  data-testid="meta-captacoes-input"
                />
              </div>
              <div>
                <Label>Estudos de Mercado</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.metas_proxima_semana.estudos}
                  onChange={(e) => setFormData({
                    ...formData,
                    metas_proxima_semana: { ...formData.metas_proxima_semana, estudos: parseInt(e.target.value) || 0 }
                  })}
                  data-testid="meta-estudos-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Submit Button */}
          <div className="flex justify-end space-x-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
              data-testid="cancel-button"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="bg-remax-blue hover:bg-[#002C7A]"
              disabled={saving}
              data-testid="save-button"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Salvando...' : 'Salvar Scorecard'}
            </Button>
          </div>

          {/* Previous Scorecards */}
          {scorecards.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Work Sans' }}>Scorecards Anteriores</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {scorecards.map((sc, index) => (
                    <div 
                      key={sc.id} 
                      className="p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 hover:shadow-md transition-all border border-transparent hover:border-blue-300" 
                      data-testid={`scorecard-${index}`}
                      onClick={() => navigate(`/scorecard/${sc.id}`)}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-blue-600">Semana {sc.semana} - Módulo {sc.modulo}</p>
                          <p className="text-sm text-gray-500">
                            {new Date(sc.periodo_inicio).toLocaleDateString('pt-BR')} a {new Date(sc.periodo_fim).toLocaleDateString('pt-BR')}
                          </p>
                          <p className="text-sm mt-1">
                            CRE: {sc.metricas.captacoes} | Visitas de Captação: {sc.metricas.visitas_1}
                          </p>
                        </div>
                        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                          sc.resumo.decisao === 'Normal' ? 'bg-green-100 text-green-800' :
                          sc.resumo.decisao === 'Alerta Amarelo' ? 'bg-yellow-100 text-yellow-800' :
                          sc.resumo.decisao === 'Alerta Vermelho' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {sc.resumo.decisao}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </form>
      </main>
    </div>
  );
}
